import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBadgeCheck } from "@/hooks/useBadges";

export type TimerMode = "pomodoro" | "countdown";
export type TimerPhase = "idle" | "focus" | "break" | "long_break";

const POMODORO_FOCUS = 25 * 60;
const POMODORO_BREAK = 5 * 60;
const POMODORO_LONG_BREAK = 15 * 60;
const CYCLES_BEFORE_LONG = 4;

const LS_KEY = "grounded_work_timer";

interface PersistedState {
  mode: TimerMode;
  phase: TimerPhase;
  timeRemaining: number;
  totalFocusSeconds: number;
  totalBreakSeconds: number;
  cyclesCompleted: number;
  targetMinutes: number | null;
  isPaused: boolean;
  lastTickAt: number; // Date.now() of last tick
  cafeId: string | null;
  cafeName: string | null;
  checkInId: string | null;
  sessionDbId: string | null;
}

export interface TimerState {
  mode: TimerMode;
  phase: TimerPhase;
  timeRemaining: number;
  totalFocusSeconds: number;
  totalBreakSeconds: number;
  cyclesCompleted: number;
  targetMinutes: number | null;
  isPaused: boolean;
  isRunning: boolean;
  cafeId: string | null;
  cafeName: string | null;
  checkInId: string | null;
}

function loadState(): PersistedState | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveState(state: PersistedState | null) {
  if (!state) {
    localStorage.removeItem(LS_KEY);
  } else {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  }
}

function getPhaseLabel(phase: TimerPhase): string {
  switch (phase) {
    case "focus": return "Focus Time";
    case "break": return "Short Break";
    case "long_break": return "Long Break";
    default: return "";
  }
}

export function useWorkTimer() {
  const { user } = useAuth();
  const { checkBadges } = useBadgeCheck();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [mode, setModeState] = useState<TimerMode>("pomodoro");
  const [phase, setPhase] = useState<TimerPhase>("idle");
  const [timeRemaining, setTimeRemaining] = useState(POMODORO_FOCUS);
  const [totalFocusSeconds, setTotalFocusSeconds] = useState(0);
  const [totalBreakSeconds, setTotalBreakSeconds] = useState(0);
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [targetMinutes, setTargetMinutes] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [cafeId, setCafeId] = useState<string | null>(null);
  const [cafeName, setCafeName] = useState<string | null>(null);
  const [checkInId, setCheckInId] = useState<string | null>(null);
  const [sessionDbId, setSessionDbId] = useState<string | null>(null);

  const isRunning = phase !== "idle";

  // Restore persisted state on mount
  useEffect(() => {
    const saved = loadState();
    if (!saved || saved.phase === "idle") return;

    setModeState(saved.mode);
    setPhase(saved.phase);
    setTotalFocusSeconds(saved.totalFocusSeconds);
    setTotalBreakSeconds(saved.totalBreakSeconds);
    setCyclesCompleted(saved.cyclesCompleted);
    setTargetMinutes(saved.targetMinutes);
    setIsPaused(saved.isPaused);
    setCafeId(saved.cafeId);
    setCafeName(saved.cafeName);
    setCheckInId(saved.checkInId);
    setSessionDbId(saved.sessionDbId);

    // Adjust time for elapsed period while away
    if (!saved.isPaused) {
      const elapsed = Math.floor((Date.now() - saved.lastTickAt) / 1000);
      setTimeRemaining(Math.max(0, saved.timeRemaining - elapsed));
    } else {
      setTimeRemaining(saved.timeRemaining);
    }
  }, []);

  // Persist state on changes
  const persistState = useCallback(() => {
    if (phase === "idle") {
      saveState(null);
      return;
    }
    saveState({
      mode, phase, timeRemaining, totalFocusSeconds, totalBreakSeconds,
      cyclesCompleted, targetMinutes, isPaused,
      lastTickAt: Date.now(),
      cafeId, cafeName, checkInId, sessionDbId,
    });
  }, [mode, phase, timeRemaining, totalFocusSeconds, totalBreakSeconds, cyclesCompleted, targetMinutes, isPaused, cafeId, cafeName, checkInId, sessionDbId]);

  useEffect(() => {
    persistState();
  }, [persistState]);

  // Send browser notification
  const notify = useCallback((title: string, body: string) => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(title, { body, icon: "/pwa-192x192.png" });
    }
  }, []);

  // Save session to DB
  const saveSession = useCallback(async (focusRating?: number) => {
    if (!user) return;

    const payload = {
      user_id: user.id,
      cafe_id: cafeId,
      check_in_id: checkInId,
      mode,
      total_focus_seconds: totalFocusSeconds,
      total_break_seconds: totalBreakSeconds,
      pomodoro_cycles_completed: cyclesCompleted,
      target_minutes: targetMinutes,
      focus_rating: focusRating ?? null,
      is_completed: true,
      ended_at: new Date().toISOString(),
    };

    let savedId = sessionDbId;
    if (sessionDbId) {
      await supabase.from("work_sessions").update(payload).eq("id", sessionDbId);
    } else {
      const { data } = await supabase.from("work_sessions").insert(payload).select("id").single();
      if (data) savedId = data.id;
    }

    // Post to community feed if session was 10+ minutes and at a cafe
    const durationMinutes = Math.round(totalFocusSeconds / 60);
    if (cafeId && totalFocusSeconds >= 600) {
      await supabase.from("feed_items").insert({
        type: "focus_session" as any,
        user_id: user.id,
        cafe_id: cafeId,
        reference_id: savedId,
        metadata: {
          duration_minutes: durationMinutes,
          mode,
          cycles_completed: cyclesCompleted,
        },
      });
    }

    // Check for focus session badges
    if (user) {
      checkBadges(user.id, 'focus_session', cafeId || undefined);
    }
  }, [user, cafeId, checkInId, mode, totalFocusSeconds, totalBreakSeconds, cyclesCompleted, targetMinutes, sessionDbId, checkBadges]);

  // Handle phase completion
  const handlePhaseEnd = useCallback(() => {
    if (mode === "countdown") {
      notify("Session Complete!", "Great focus session! 🎉");
      if (totalFocusSeconds > 10) {
        saveSession();
      }
      setPhase("idle");
      return;
    }

    // Pomodoro mode
    if (phase === "focus") {
      const newCycles = cyclesCompleted + 1;
      setCyclesCompleted(newCycles);
      if (newCycles % CYCLES_BEFORE_LONG === 0) {
        setPhase("long_break");
        setTimeRemaining(POMODORO_LONG_BREAK);
        notify("Long Break!", "You've earned a 15 minute break ☕");
      } else {
        setPhase("break");
        setTimeRemaining(POMODORO_BREAK);
        notify("Break Time!", "Take a 5 minute break 🧘");
      }
    } else {
      setPhase("focus");
      setTimeRemaining(POMODORO_FOCUS);
      notify("Focus Time!", "Back to work! 💪");
    }
  }, [mode, phase, cyclesCompleted, notify, totalFocusSeconds, saveSession]);

  // Timer tick
  useEffect(() => {
    if (phase === "idle" || isPaused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handlePhaseEnd();
          return 0;
        }
        return prev - 1;
      });

      if (phase === "focus") {
        setTotalFocusSeconds(prev => prev + 1);
      } else {
        setTotalBreakSeconds(prev => prev + 1);
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [phase, isPaused, handlePhaseEnd]);

  const startTimer = useCallback(async (opts: {
    mode: TimerMode;
    targetMinutes?: number;
    cafeId?: string;
    cafeName?: string;
    checkInId?: string;
  }) => {
    // Request notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    const m = opts.mode;
    const target = opts.targetMinutes || 25;
    setModeState(m);
    setPhase("focus");
    setTimeRemaining(m === "pomodoro" ? POMODORO_FOCUS : target * 60);
    setTotalFocusSeconds(0);
    setTotalBreakSeconds(0);
    setCyclesCompleted(0);
    setTargetMinutes(m === "countdown" ? target : null);
    setIsPaused(false);
    setCafeId(opts.cafeId || null);
    setCafeName(opts.cafeName || null);
    setCheckInId(opts.checkInId || null);

    // Create DB record
    if (user) {
      const { data } = await supabase.from("work_sessions").insert({
        user_id: user.id,
        cafe_id: opts.cafeId || null,
        check_in_id: opts.checkInId || null,
        mode: m,
        target_minutes: m === "countdown" ? target : null,
      }).select("id").single();
      if (data) setSessionDbId(data.id);
    }
  }, [user]);

  const pauseTimer = useCallback(() => setIsPaused(true), []);
  const resumeTimer = useCallback(() => setIsPaused(false), []);

  const stopTimer = useCallback(async (focusRating?: number) => {
    if (totalFocusSeconds > 10) {
      await saveSession(focusRating);
    }
    setPhase("idle");
    setTimeRemaining(0);
    setTotalFocusSeconds(0);
    setTotalBreakSeconds(0);
    setCyclesCompleted(0);
    setIsPaused(false);
    setCafeId(null);
    setCafeName(null);
    setCheckInId(null);
    setSessionDbId(null);
    saveState(null);
  }, [totalFocusSeconds, saveSession]);

  const skipPhase = useCallback(() => {
    handlePhaseEnd();
  }, [handlePhaseEnd]);

  const setMode = useCallback((m: TimerMode) => {
    if (phase !== "idle") return;
    setModeState(m);
    setTimeRemaining(m === "pomodoro" ? POMODORO_FOCUS : (targetMinutes || 25) * 60);
  }, [phase, targetMinutes]);

  const setCountdownDuration = useCallback((minutes: number) => {
    if (phase !== "idle") return;
    setTargetMinutes(minutes);
    if (mode === "countdown") {
      setTimeRemaining(minutes * 60);
    }
  }, [phase, mode]);

  const timerState: TimerState = {
    mode, phase, timeRemaining, totalFocusSeconds, totalBreakSeconds,
    cyclesCompleted, targetMinutes, isPaused,
    isRunning, cafeId, cafeName, checkInId,
  };

  return {
    timerState,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    skipPhase,
    setMode,
    setCountdownDuration,
    getPhaseLabel: () => getPhaseLabel(phase),
  };
}
